import { ArrowLeft } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Card } from "../../ui/card";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  getAllGlamPackages,
  getDuration,
  getDurationForPackage,
  getGlamPackage,
} from "../../../services/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAddBookingForm } from "../../../hooks/useAdminBookingForm";
import { CustomDatePicker } from "../../custom-date-picker";
import { CustomTimePicker } from "../../custom-time-picker";

export function AddBookingForm() {
  const navigate = useNavigate();
  const [duration, setDuration] = useState<{ hours: number; minutes: number }>({
    hours: 4,
    minutes: 0,
  });

  const {
    glamPackages,
    clientPhotoPreview,
    customerName,
    selectedPackage,
    glamSpot,
    numberOfFaces,
    showCustomFacesInput,
    customFacesCount,
    email,
    emailError,
    phoneNumber,
    additionalInfo,
    handleEmailChange,
    setGlamPackages,
    setSelectedPackage,
    setCustomerName,
    setGlamSpot,
    handleNumberOfFacesChange,
    handleCustomFacesChange,
    setEmailError,
    setPhoneNumber,
    setAdditionalInfo,
    fileInputRef,
    dateSelection,
    timeSelection,
    handleDateSelect,
    handleTimeSelect,
    validateEmail,
    handleImageUpload,
    handleSubmit,
    homeAddress,
    setHomeAddress,
    landmark,
    setLandmark,
  } = useAddBookingForm({
    onBack: () => navigate("/control/bookings"),
    duration,
  });

  useEffect(() => {
    const fetchDuration = async () => {
      if (!selectedPackage) return;
      
      try {
        const response = await getGlamPackage(selectedPackage) as any;
        setDuration(response.data.data.duration);
      } catch (error) {
        console.error("Failed to fetch duration for package, using defaults", error);
      }
    };
  
    fetchDuration();
  }, [selectedPackage]); // Run when selectedPackage changes

  useEffect(() => {
    const fetchGlamPackages = async () => {
      try {
        const response = (await getAllGlamPackages()) as any;
        setGlamPackages(response.data.data);
      } catch (error) {
        console.error("Failed to fetch glam packages:", error);
        toast.error("Failed to fetch glam packages.", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    };

    fetchGlamPackages();
  }, []);

  return (
    <div className="space-y-6 mb-4">
      <ToastContainer />
      <div className="flex items-center gap-2 text-[#B87775]">
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => navigate("/control/bookings")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="font-medium">ADD BOOKING</span>
      </div>

      <div className="grid lg:grid-cols-[1fr,400px] gap-6">
        <div className="space-y-6">
          <div className="">
            {/* Image Upload */}
            <div
              className="border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#B87775] transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {clientPhotoPreview ? (
                <img
                  src={clientPhotoPreview}
                  alt="Client Photo Preview"
                  className="w-full h-full object-cover rounded-lg"
                />
              ) : (
                <div className="text-center">
                  <div className="text-2xl mb-2">+</div>
                  <div className="text-sm text-muted-foreground">Add Image</div>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
            </div>
          </div>

          {/* Form Fields */}

          <div className="flex flex-col gap-4">
            <div className="space-y-2">
              <Label>Customer Name</Label>
              <Input
                placeholder="Enter customer name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>

            <div className="">
              <Label>Glam Package</Label>
              <Select
                value={selectedPackage}
                onValueChange={setSelectedPackage}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a Package" />
                </SelectTrigger>
                <SelectContent className="text-black">
                  {glamPackages.map((pkg) => (
                    <SelectItem
                      key={pkg.id}
                      value={pkg.id}
                      className="text-black"
                    >
                      {pkg.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Glam Spot</Label>
              <Select value={glamSpot} onValueChange={setGlamSpot}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="studio">Studio</SelectItem>
                  <SelectItem value="home">Home</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {glamSpot === "home" && (
              <>
                <div className="space-y-2">
                  <Label>
                    Home Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    placeholder="Enter full home address"
                    value={homeAddress}
                    onChange={(e) => setHomeAddress(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Landmark (Optional)</Label>
                  <Input
                    placeholder="Nearby landmark for easier location"
                    value={landmark}
                    onChange={(e) => setLandmark(e.target.value)}
                  />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Faces</Label>
              <Select
                value={numberOfFaces}
                onValueChange={handleNumberOfFacesChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select number of faces" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num}
                    </SelectItem>
                  ))}
                  <SelectItem value="more">More than 5</SelectItem>
                </SelectContent>
              </Select>

              {showCustomFacesInput && (
                <div className="mt-2">
                  <Label>Exact number of faces</Label>
                  <Input
                    type="number"
                    min="6"
                    value={customFacesCount}
                    onChange={(e) => handleCustomFacesChange(e.target.value)}
                    placeholder="Enter number (6 or more)"
                  />
                </div>
              )}
            </div>
            {/* Date and Time fields */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="date">Glam Date</Label>
                <CustomDatePicker
                  selectedDate={dateSelection.selectedDate}
                  onDateSelect={handleDateSelect}
                  error={
                    !dateSelection.isAvailable &&
                    dateSelection.selectedDate !== ""
                  }
                />
                {!dateSelection.isAvailable &&
                  dateSelection.selectedDate &&
                  !dateSelection.isLoading && ( // Ensure it's not still checking availability
                    <p className="text-red-500 text-sm">Date not available</p>
                  )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Glam Time</Label>
                <CustomTimePicker
                  selectedTime={timeSelection.selectedTime24h}
                  onTimeSelect={handleTimeSelect}
                  isLoading={timeSelection.isLoading}
                  isDisabled={
                    !dateSelection.isAvailable || dateSelection.isLoading
                  }
                  error={
                    Boolean(timeSelection.selectedTime24h) &&
                    !timeSelection.isAvailable
                  }
                />
                {timeSelection.selectedTime24h &&
                  !timeSelection.isAvailable &&
                  !timeSelection.isLoading && (
                    <p className="text-red-500 text-sm">
                      Time slot not available
                    </p>
                  )}
                {!dateSelection.isAvailable && (
                  <p className="text-gray-500 text-sm">
                    Please select an available date first
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="eg customeremail@gmail.com"
                value={email}
                onChange={handleEmailChange}
                onBlur={() => {
                  if (email && !validateEmail(email)) {
                    setEmailError("Please enter a valid email address");
                  }
                }}
              />
              {emailError && (
                <p className="text-red-500 text-sm mt-1">{emailError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="eg +1 (416) 555-2486"
                  value={phoneNumber}
                  maxLength={15}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                  }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Additional Info</Label>
              <Input
                placeholder="Any additional information"
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
              />
            </div>
          </div>

          <Button
            className="bg-[#B87775] hover:bg-[#B87775]/90 text-white w-full"
            onClick={handleSubmit}
          >
            Create Booking
          </Button>
        </div>

        {/* Confirmation Details */}
        <Card className="p-6 sticky top-20 self-start overflow-y-auto">
          <h2 className="text-lg font-semibold mb-2">Confirmation Details</h2>
          <p className="text-sm text-muted-foreground mb-6">
            These information will be sent to customer's mail after confirmation
          </p>

          <div className="space-y-4">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">{customerName}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Package</span>
              <span className="font-medium">
                {glamPackages.find((pkg) => pkg.id === selectedPackage)
                  ?.title || "N/A"}
              </span>
            </div>
            {glamSpot === "home" && (
              <>
                <div className="flex justify-between py-2 border-b">
                  <span className="text-muted-foreground">Home Address</span>
                  <span className="font-medium">{homeAddress}</span>
                </div>
                {landmark && (
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Landmark</span>
                    <span className="font-medium">{landmark}</span>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Glam Time</span>
              <span className="font-medium">
                {timeSelection.selectedTime24h}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Glam Date</span>
              <span className="font-medium">{dateSelection.selectedDate}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Session Fee</span>
              <span className="font-medium">
                $
                {glamPackages.find((pkg) => pkg.id === selectedPackage)
                  ?.price || "N/A"}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Number of Faces</span>
              <span className="font-medium">{numberOfFaces || customFacesCount}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Total</span>
              <span className="font-medium">
                $
                {glamPackages.find((pkg) => pkg.id === selectedPackage)
                  ? glamPackages.find((pkg) => pkg.id === selectedPackage)!
                      .price * parseInt(numberOfFaces, 10)
                  : "N/A"}
              </span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
